import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import { formatCurrency } from "../../utils/namesFormater";
import { BeneficiaryInfo } from "../../interfaces/DTO/mislaka_xml_insurence_models";

interface BeneficiariesMetricsProps {
  distinctBeneficiaries: BeneficiaryInfo[];
  beneficiaryTotals: { [key: string]: number };
  beneficiaryDeathCoverageTotals: { [key: string]: number };
  totalWithoutBeneficiaries: number;
  totalWithoutBeneficiariesDeathCoverage: number;
  totalLegalHeirs: number;
  totalLegalHeirsDeathCoverage: number;
}

export const BeneficiariesMetrics: React.FC<BeneficiariesMetricsProps> = ({
  distinctBeneficiaries,
  beneficiaryTotals,
  beneficiaryDeathCoverageTotals,
  totalWithoutBeneficiaries,
  totalWithoutBeneficiariesDeathCoverage,
  totalLegalHeirs,
  totalLegalHeirsDeathCoverage,
}) => {
  return (
    <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
      {/* סכום כולל ללא מוטבים */}
      {(totalWithoutBeneficiaries > 0 || totalWithoutBeneficiariesDeathCoverage > 0) && (
        <Paper
          elevation={2}
          sx={{
            p: 2,
            textAlign: "center",
            backgroundColor: "#f5f5f5",
            width: "200px",
            flexShrink: 0
          }}
        >
          <Typography variant="caption" color="text.secondary">
            סכום כולל ללא מוטבים
          </Typography>
          {totalWithoutBeneficiaries > 0 && (
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#d32f2f" }}>
              {formatCurrency(totalWithoutBeneficiaries)}
            </Typography>
          )}
          {totalWithoutBeneficiariesDeathCoverage > 0 && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                סכום מביטוח ללא מוטבים
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#d32f2f", textAlign: 'center' }}>
                {formatCurrency(totalWithoutBeneficiariesDeathCoverage)}
              </Typography>
            </>
          )}
        </Paper>
      )}

      {/* סכום כולל ליורשים חוקיים */}
      {(totalLegalHeirs > 0 || totalLegalHeirsDeathCoverage > 0) && (
        <Paper
          elevation={2}
          sx={{
            p: 2,
            textAlign: "center",
            backgroundColor: "#fff3e0",
            width: "200px",
            flexShrink: 0
          }}
        >
          <Typography variant="caption" color="text.secondary">
            יורשים חוקיים
          </Typography>
          {totalLegalHeirs > 0 && (
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#f57c00" }}>
              {formatCurrency(totalLegalHeirs)}
            </Typography>
          )}
          {totalLegalHeirsDeathCoverage > 0 && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                סכום מביטוח
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#f57c00", textAlign: 'center' }}>
                {formatCurrency(totalLegalHeirsDeathCoverage)}
              </Typography>
            </>
          )}
        </Paper>
      )}

      {/* סכום כולל לכל מוטב */}
      {distinctBeneficiaries.map(beneficiary => {
        const firstName = beneficiary.first_name || "";
        const lastName = beneficiary.last_name || "";
        // אם שם פרטי זהה לשם משפחה, הצג רק את השם הפרטי
        const beneficiaryName = firstName && lastName && firstName === lastName
          ? firstName
          : `${firstName} ${lastName}`.trim() || beneficiary.id_number || "מוטב";
        const total = beneficiaryTotals[beneficiary.id_number || ""] || 0;
        const deathCoverageTotal = beneficiaryDeathCoverageTotals[beneficiary.id_number || ""] || 0;

        // הצג את המוטב אם יש לו סכום מחיסכון או מביטוח
        if (total === 0 && deathCoverageTotal === 0) return null;

        return (
          <Paper
            key={beneficiary.id_number}
            elevation={2}
            sx={{
              p: 2,
              textAlign: "center",
              backgroundColor: "#e3f2fd",
              width: "200px",
              flexShrink: 0
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {beneficiaryName}
            </Typography>
            {total > 0 && (
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#1976d2" }}>
                {formatCurrency(total)}
              </Typography>
            )}
            {deathCoverageTotal > 0 && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  סכום מביטוח
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#1976d2", textAlign: 'center' }}>
                  {formatCurrency(deathCoverageTotal)}
                </Typography>
              </>
            )}
          </Paper>
        );
      })}
    </Box>
  );
};
